import React, { useState, useEffect, useMemo, useCallback} from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, query, getDocs, doc, setDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import KanbanBoard from '../components/KanbanBoard';
import FormularioDinamico from '../components/FormularioDinamico';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../../context/AuthContext';

const Amostras = () => {
    const { workspaceData } = useOutletContext();
    const { currentUser } = useAuth();
    const [view, setView] = useState('KANBAN'); 
    const [amostras, setAmostras] = useState([]);
    const [obrasList, setObrasList] = useState([]); 
    const [obrasFullData, setObrasFullData] = useState([]); 
    const [isProcessing, setIsProcessing] = useState(false);

    // --- RBAC: Verificação de Nível de Acesso (Anti-Hacker Visual) ---
    const isExtranetView = workspaceData?.role === 'EXTRANET';

    // --- CARREGAMENTO DE DADOS (3FN) ---
    const carregarDados = useCallback(async () => {
        if (!workspaceData?.cnpj_completo) return;
        setIsProcessing(true);
        try {
            const qAmostras = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_amostras'), orderBy('created_at', 'desc'));
            const snapAmostras = await getDocs(qAmostras);
            const listaAmostras = snapAmostras.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAmostras(listaAmostras);

            if (!isExtranetView) {
                const qObras = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_obras'));
                const snapObras = await getDocs(qObras);
                const rawObras = snapObras.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setObrasFullData(rawObras);
                setObrasList(rawObras.map(o => ({ label: o.titulo, value: o.id })));
            }
        } catch (error) {
            console.error("Erro ao carregar amostra:", error);
            alert("Erro ao sincronizar amostras. Verifique sua conexão.");
        } finally {
            setIsProcessing(false);
        }
    }, [workspaceData?.cnpj_completo, isExtranetView]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    // --- DEFINIÇÃO DO KANBAN ---
    const kanbanColumns = [
        { id: 'COLETADA', title: 'Coletada (Pista/Jazida)', color: '#94a3b8' },
        { id: 'EM_TRANSITO', title: 'Em Trânsito', color: '#f59e0b' },
        { id: 'RECEBIDA_LAB', title: 'Recebida no Laboratório', color: '#3b82f6' },
        { id: 'EM_ENSAIO', title: 'Na Central de Ensaios', color: '#8b5cf6' }, // A IA assume a partir daqui
        { id: 'DESCARTADA', title: 'Amostra Descartada', color: '#ef4444' }
    ];

    const handleImprimirEtiqueta = (item, obraNome) => {
        // Motor de Impressão (Pop-up isolado para Térmica Zebra/Argox)
        const printWindow = window.open('', '_blank', 'width=400,height=300');
        const qrSvg = document.getElementById(`qr-${item.id}`).outerHTML;
        const dataColeta = item.data_coleta ? new Date(item.data_coleta).toLocaleDateString('pt-BR') : 'N/A';
        
        printWindow.document.write(`
            <html>
                <head>
                    <title>Etiqueta LIMS</title>
                    <style>
                        body { 
                            margin: 0; padding: 0; font-family: monospace; 
                            width: 60mm; height: 40mm; /* Padrão Etiqueta Laboratório */
                            display: flex; flex-direction: column; align-items: center; justify-content: center;
                        }
                        .qr-container { margin-bottom: 3px; }
                        .qr-container svg { width: 25mm; height: 25mm; }
                        h1 { margin: 0; font-size: 14px; font-weight: bold; }
                        p { margin: 0; font-size: 9px; text-align: center; }
                        @media print {
                            @page { margin: 0; size: 60mm 40mm; }
                        }
                    </style>
                </head>
                <body onload="window.print(); window.close();">
                    <div class="qr-container">${qrSvg}</div>
                    <h1>${item.id}</h1>
                    <p>${item.material_alvo} - ${dataColeta}</p>
                    <p>${obraNome.substring(0, 20)}</p>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    const renderCartaoAmostra = (item) => {
        const obraNome = obrasList.find(o => o.value === item.obra_id)?.label || 'Obra Externa';
        
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div style={{ background: '#fff', padding: '4px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <QRCodeSVG id={`qr-${item.id}`} value={item.id} size={32} />
                        </div>
                        <strong style={{ color: '#0f172a', fontSize: '0.95rem' }}>{item.id}</strong>
                    </div>
                    <button 
                        onClick={() => handleImprimirEtiqueta(item, obraNome)}
                        style={{ border: 'none', background: '#f1f5f9', color: '#0369a1', cursor: 'pointer', borderRadius: '4px', padding: '4px 8px', fontSize: '0.75rem', fontWeight: 'bold' }}
                        title="Imprimir Etiqueta Térmica"
                    >🖨️ Imprimir</button>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#475569', lineHeight: '1.4' }}>
                    📍 <strong>Obra:</strong> {obraNome}<br/>
                    🛣️ <strong>Local:</strong> {item.rodovia_rua ? `${item.rodovia_rua} (${item.sentido_lado})` : 'N/A'}<br/>
                    📏 <strong>KM/Estaca:</strong> {item.estaca_km}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>📅 {item.data_coleta ? new Date(item.data_coleta).toLocaleDateString('pt-BR') : 'Data N/A'}</span>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>{item.material_alvo}</span>
                </div>
            </div>
        );
    };

    const handleMoverAmostra = async (itemMovido, novaColunaId) => {
        if (isExtranetView) return; // Bloqueio Frontend

        try {
            const amostraRef = doc(db, 'workspaces', workspaceData.cnpj_completo, 'crm_amostras', itemMovido.id);
            // Atualização Atômica 3FN: Muda apenas o status.
            await setDoc(amostraRef, { status: novaColunaId, updated_at: serverTimestamp() }, { merge: true });
            
            setAmostras(prev => prev.map(a => a.id === itemMovido.id ? { ...a, status: novaColunaId } : a));
        } catch (error) {
            alert("Erro de permissão no Firestore: " + error.message);
        }
    };

    // Estado local para observar gatilhos dinâmicos do formulário
    const [materialSelecionado, setMaterialSelecionado] = useState('CBUQ'); 
    const [localColeta, setLocalColeta] = useState('PISTA'); // PISTA ou FONTE

    // --- DEFINIÇÃO DO FORMULÁRIO DE INGESTÃO CAMALEÃO (Nível Produção DNIT) ---
    const camposForm = useMemo(() => {
        // Bloco Base 1: O que é comum a TUDO
        const baseFields = [
            { name: 'obra_id', label: 'Vincular à Obra', type: 'select', required: true, colSpan: 12, options: [
                { label: 'Selecione a Obra já cadastrada, ou gere uma nova na aba "Gestão de Obras"', value: '' },
                ...obrasList
            ]},
            { name: 'material_alvo', label: 'Tipo de Material Físico', type: 'select', required: true, colSpan: 6, options: [
                { label: 'CBUQ / Mistura Betuminosa', value: 'CBUQ' },
                { label: 'Solo / Agregados (Naturais ou Britados)', value: 'SOLO_AGREGADO' },
                { label: 'Cimento Portland / Concreto', value: 'CIMENTO_CONCRETO' },
                { label: 'Emulsão / CAP (Ligante)', value: 'LIGANTE' },
                { label: 'Tinta Viária', value: 'TINTA' }
            ]},
            { name: 'data_coleta', label: 'Data da Coleta/Recebimento', type: 'date', required: true, colSpan: 6 },
            { name: 'divider_geo', label: 'ORIGEM FÍSICA E RASTREABILIDADE', type: 'title', colSpan: 12 }
        ];

        let dynamicFields = [];

        // GRUPO A: Materiais Estruturais (Podem vir da rodovia/pista ou da usina/jazida)
        if (materialSelecionado === 'CBUQ' || materialSelecionado === 'SOLO_AGREGADO') {
            dynamicFields.push({ 
                name: 'local_coleta', label: 'Local Exato da Coleta', type: 'select', required: true, colSpan: 12, options: [
                    { label: 'Na Pista / Trecho Rodoviário (Ex: CBUQ aplicado, Base compactada)', value: 'PISTA' },
                    { label: 'Na Fonte / Produção (Ex: Usina de Asfalto, Jazida, Pedreira)', value: 'FONTE' }
                ]
            });

            if (localColeta === 'PISTA') {
                dynamicFields.push(
                    { name: 'rodovia_rua', label: 'Rodovia / Logradouro', type: 'text', required: true, colSpan: 4, placeholder: 'Ex: BR-116' },
                    { name: 'estaca_km', label: 'Estaca / KM', type: 'text', required: true, colSpan: 4, placeholder: 'Ex: KM 102+500' },
                    { name: 'sentido_lado', label: 'Sentido / Lado', type: 'select', required: true, colSpan: 4, options: [
                        { label: 'Selecione...', value: '' }, { label: 'Norte', value: 'NORTE' }, { label: 'Sul', value: 'SUL' },
                        { label: 'Leste', value: 'LESTE' }, { label: 'Oeste', value: 'OESTE' }, { label: 'Eixo / Pista Dupla', value: 'EIXO' }
                    ]},
                    { name: 'camada_aplicacao', label: 'Camada Extraída/Aplicada', type: 'select', required: true, colSpan: 12, options: [
                        { label: 'Capa de Rolamento / Revestimento', value: 'REVESTIMENTO' },
                        { label: 'Base', value: 'BASE' },
                        { label: 'Sub-base', value: 'SUBBASE' },
                        { label: 'Subleito / Reforço', value: 'SUBLEITO' },
                        { label: 'Outro', value: 'OUTRO' }
                    ]}
                );
            } else {
                // Coletado na Fonte de Produção ou Natureza
                dynamicFields.push(
                    { name: 'origem_jazida_silo', label: 'Nome da Fonte (Usina / Pedreira / Jazida)', type: 'text', required: true, colSpan: 6, placeholder: 'Ex: Pedreira Santa Luzia' },
                    { name: 'identificacao_traco', label: 'Identificação (Furo / Lote / Traço)', type: 'text', required: true, colSpan: 6, placeholder: 'Ex: Furo 02 ou Traço CBUQ Faixa C' }
                );
            }
        } 
        // GRUPO B: Materiais Industrializados de Recebimento (Cimento, Ligante, Tinta)
        else if (['LIGANTE', 'TINTA', 'CIMENTO_CONCRETO'].includes(materialSelecionado)) {
            dynamicFields = [
                { name: 'origem_jazida_silo', label: 'Fabricante / Fornecedor', type: 'text', required: true, colSpan: 5, placeholder: 'Ex: Betume S.A.' },
                { name: 'lote_producao', label: 'Lote / Corrida', type: 'text', required: true, colSpan: 4, placeholder: 'Ex: Lote 45020' },
                { name: 'nota_fiscal', label: 'Nota Fiscal (NF-e)', type: 'text', required: false, colSpan: 3, placeholder: 'Opcional' }
            ];
        }

        return [...baseFields, ...dynamicFields, { name: 'observacoes', label: 'Observações do Material (Opcional)', type: 'textarea', colSpan: 12 }];
    }, [obrasList, materialSelecionado, localColeta]);

    // Escuta ativa das digitações do usuário no formulário filho
    const handleFieldChange = (name, value) => {
        if (name === 'material_alvo') {
            setMaterialSelecionado(value);
            // Ao trocar o material, forçamos o local default de volta para Pista para não quebrar a UI
            setLocalColeta('PISTA'); 
        }
        if (name === 'local_coleta') {
            setLocalColeta(value);
        }
    };

    const handleSaveAmostra = async (formData) => {
        setIsProcessing(true);
        try {
            const obraSelecionada = obrasFullData.find(o => o.id === formData.obra_id);
            const clienteVisibilidade = obraSelecionada ? obraSelecionada.empresa_id : null;

            // GERAÇÃO ATÔMICA DO ID DO QR CODE
            const baseRef = doc(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_amostras'));
            const hashGerado = "AM-" + baseRef.id.substring(0, 8).toUpperCase(); 
            const amostraRef = doc(db, 'workspaces', workspaceData.cnpj_completo, 'crm_amostras', hashGerado);

            // Filtro Purificador: Remove sujeiras do state se o usuário ficou alternando entre abas
            const payloadLimpo = { 
                obra_id: formData.obra_id,
                material_alvo: formData.material_alvo,
                data_coleta: formData.data_coleta,
                observacoes: formData.observacoes || ''
            };

            // Injeta estritamente os campos que estavam visíveis baseados na escolha
            if (['CBUQ', 'SOLO_AGREGADO'].includes(formData.material_alvo)) {
                payloadLimpo.local_coleta = formData.local_coleta;
                if (formData.local_coleta === 'PISTA') {
                    payloadLimpo.rodovia_rua = formData.rodovia_rua;
                    payloadLimpo.estaca_km = formData.estaca_km;
                    payloadLimpo.sentido_lado = formData.sentido_lado;
                    payloadLimpo.camada_aplicacao = formData.camada_aplicacao;
                } else {
                    payloadLimpo.origem_jazida_silo = formData.origem_jazida_silo;
                    payloadLimpo.identificacao_traco = formData.identificacao_traco;
                }
            } else {
                payloadLimpo.origem_jazida_silo = formData.origem_jazida_silo;
                payloadLimpo.lote_producao = formData.lote_producao;
                payloadLimpo.nota_fiscal = formData.nota_fiscal || '';
            }

            await setDoc(amostraRef, {
                ...payloadLimpo,
                status: 'COLETADA', // Entra obrigatoriamente no início do funil LIMS
                cliente_cnpj_visibilidade: clienteVisibilidade, // A tranca do Hacker liberada para o cliente
                created_at: serverTimestamp(),
                autor_uid: currentUser?.uid || "UID_NAO_IDENTIFICADO" // <-- 3. CORREÇÃO ANTI-CRASH (TRILHA DE AUDITORIA REAL)
            });

            alert(`✅ Amostra (${hashGerado}) registrada. Pronta para ser bipada na Central de Ensaios.`);
            setView('KANBAN');
            carregarDados();
        } catch (e) {
            alert("Falha de segurança ou conexão: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        /* Container Principal: Ocupa 100% da altura disponível no PortalShell */
        <div style={{ 
            padding: '20px 30px', 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            background: '#f8fafc',
            boxSizing: 'border-box'
        }}>
            
            {/* CABEÇALHO: Altura fixa, não encolhe */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px', 
                flexShrink: 0 
            }}>
                <div>
                    <h2 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.6rem', fontWeight: '800' }}>
                        Gestão de Amostras (triagem)
                    </h2>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.9rem' }}>
                        {isExtranetView 
                            ? "Rastreabilidade de materiais em análise."
                            : "Gestão logística e entrada de materiais via QR Code."}
                    </p>
                </div>
                {!isExtranetView && (
                    <button 
                        className="pts-btn-primary" 
                        onClick={() => setView(view === 'KANBAN' ? 'FORM' : 'KANBAN')}
                        style={{ padding: '10px 20px', fontSize: '0.9rem' }}
                    >
                        {view === 'KANBAN' ? '+ Nova Amostra' : '← Voltar ao Kanban'}
                    </button>
                )}
            </div>

            {/* ÁREA DE CONTEÚDO: Expande para ocupar o resto da tela e gerencia o próprio scroll */}
            <div style={{ 
                flex: 1, 
                minHeight: 0, // CRÍTICO: Permite que o flex child encolha e ative o scroll interno
                display: 'flex',
                flexDirection: 'column'
            }}>
                
                {view === 'KANBAN' ? (
                    /* O KanbanBoard já deve lidar com overflow-x interno */
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <KanbanBoard 
                            columns={kanbanColumns}
                            items={amostras}
                            groupByKey="status"
                            renderCard={renderCartaoAmostra}
                            onItemMove={isExtranetView ? null : handleMoverAmostra}
                        />
                    </div>
                ) : (
                    /* Container do Formulário com Scroll Vertical Garantido */
                    <div style={{ 
                        flex: 1,
                        overflowY: 'auto', 
                        paddingBottom: '40px',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <div style={{ 
                            width: '100%',
                            maxWidth: '800px', 
                            background: '#fff', 
                            padding: '40px', 
                            borderRadius: '16px', 
                            border: '1px solid #e2e8f0', 
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
                            height: 'fit-content' // Garante que o card não estique além do necessário
                        }}>
                            <h3 style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '25px', color: '#0369a1' }}>
                                Registro Físico de Material (QR Code)
                            </h3>
                            <FormularioDinamico 
                                fields={camposForm} 
                                onSubmit={handleSaveAmostra}
                                onCancel={() => setView('KANBAN')}
                                isProcessing={isProcessing}
                                submitLabel="Gerar ID e Iniciar Rastreio"
                                onFieldChange={handleFieldChange} // <-- ADICIONE ISSO
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default Amostras;